import { capitalize, titleCase } from "portal/utils/strings";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { classes, STATUS_TEXT } from "portal/theme/theme";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateTime } from "luxon";
import { formatDateTimeMillis } from "./measurement/formatters";
import {
  GridActionsColDef,
  GridColDef,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";
import { isUndefined } from "portal/utils/identity";
import { portalApi } from "portal/state/portalApi";
import { SearchField } from "./header/SearchField";
import { useFuzzySearch } from "portal/utils/hooks/useFuzzySearch";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import React, { FunctionComponent, useMemo } from "react";

interface ResolvedModuleAssignment {
  robotId: number;
  robotCustomerSerial: string | undefined;
  moduleId: number;
  moduleSerial: string;
  updatedAtMs: number | undefined;
}

const defaultColumn: Partial<GridColDef> = {
  sortable: true,
  disableColumnMenu: true,
};

export const FleetModulesTable: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const { data: modules, isLoading: modulesLoading } = useQueryPopups(
    portalApi.endpoints.listReaperModules.useQuery()
  );
  const { data: summaries, isLoading } = useQueryPopups(
    portalApi.endpoints.listRobots.useQuery({})
  );

  const flattenedModules = useMemo<ResolvedModuleAssignment[]>(() => {
    if (!modules) {
      return [];
    }

    return Object.entries(modules.robotIdToModules).flatMap((val) => {
      const robotId = Number.parseInt(val[0]);
      const robotCustomerSerial = isLoading
        ? t("components.Loading.placeholder")
        : summaries?.find((summary) => summary.robot?.db?.id === robotId)?.robot
            ?.customerSerial;
      return val[1].modules.map((mod) => {
        return {
          robotId,
          robotCustomerSerial,
          moduleId: mod.id,
          moduleSerial: mod.serial,
          updatedAtMs: mod.updatedAt?.timestampMs,
        };
      });
    });
  }, [modules, summaries, isLoading, t]);
  const { searchText, setSearchText, results } =
    useFuzzySearch<ResolvedModuleAssignment>({
      items: flattenedModules,
      options: {
        keys: ["moduleSerial", "robotCustomerSerial"],
      },
    });

  const columns: Array<
    GridColDef<ResolvedModuleAssignment> | GridActionsColDef
  > = [
    {
      ...defaultColumn,
      field: "robotCustomerSerial",
      headerName: t("models.robots.robot_one"),
      cellClassName: ({ value }) =>
        classes({
          "font-mono font-bold": !isLoading && value !== "Unknown",
          [STATUS_TEXT.GRAY]: isLoading || value === "Unknown",
        }),
      valueGetter: (value, _) => {
        const foundSerial =
          value as ResolvedModuleAssignment["robotCustomerSerial"];
        if (!foundSerial) {
          return t("models.robots.unknown");
        }
        return foundSerial;
      },
      width: 150,
    },
    {
      ...defaultColumn,
      field: "moduleSerial",
      headerName: t("models.modules.fields.serial.value"),
      cellClassName: ({ value }) =>
        classes({ "font-mono": value, [STATUS_TEXT.GRAY]: !value }),
      width: 150,
    },
    {
      ...defaultColumn,
      field: "moduleId",
      headerName: titleCase(t("models.modules.module_one")),
      cellClassName: "font-mono",
      sortComparator: new Intl.Collator(undefined, { numeric: true }).compare,
      width: 100,
    },
    {
      ...defaultColumn,
      field: "updatedAtMs",
      headerName: t("models.modules.fields.updatedAt"),
      cellClassName: ({ value }) =>
        classes({ "font-mono": value, [STATUS_TEXT.GRAY]: !value }),
      width: 300,
      valueFormatter: (value) => {
        return formatDateTimeMillis(t, i18n, value);
      },
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <CarbonDataGrid<ResolvedModuleAssignment>
        getRowId={(row) =>
          `${row.robotId} - ${row.moduleId} - ${row.moduleSerial}`
        }
        header={
          <>
            <SearchField
              value={searchText}
              onChange={setSearchText}
              label={t("utils.actions.searchLong", {
                subject: capitalize(
                  t("models.modules.module_other", {
                    count: flattenedModules.length,
                  })
                ),
              })}
            />
            <div className="flex items-center">
              <GridToolbarExport
                csvOptions={{
                  fileName: t("components.ModuleTable.export", {
                    robots: t("views.fleet.title"),
                    date: DateTime.local().toFormat(DATE_PATH_FORMAT),
                  }),
                }}
                excelOptions={{ disableToolbarButton: true }}
                printOptions={{ disableToolbarButton: true }}
              />
            </div>
          </>
        }
        className={classes("flex flex-1")}
        loading={modulesLoading || isUndefined(modules)}
        rows={results}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "robotSerial", sort: "asc" }],
          },
        }}
        hideFooter
        disableRowSelectionOnClick
      />
    </div>
  );
};
